import { TIMEZONE } from "@services/sdk/models/constants";
import moment, { type Moment } from "moment-timezone";
import type { IDayPortingHours, WeekPortingHours } from "../interfaces";

class PortingConfig {
	public static ConstructEmpty(): PortingConfig {
		return new PortingConfig([]);
	}

	public static Construct(weekPortingHours: WeekPortingHours): PortingConfig {
		return new PortingConfig(weekPortingHours);
	}

	constructor(weekPortingHours: WeekPortingHours) {
		this.weekPortingHours = weekPortingHours;
	}

	private readonly weekPortingHours: WeekPortingHours;

	/**
	 * Gets dayIndex as defined by MRTM API for a given timestamp.
	 * MRTM API defines dayIndexes as Monday -> 1 ... Sunday -> 7.
	 *
	 * @param timestamp
	 */
	private static getDayOfWeek(timestamp: Moment): number {
		const momentDayOfWeek = timestamp.day();

		if (momentDayOfWeek === 0) {
			return 7;
		}

		return momentDayOfWeek;
	}

	/**
	 * Converts timeStr of format "HH:MM:SS" to minutes since 00:00.
	 * Returns 0 if invalid timeStr.
	 *
	 * @param timeStr
	 */
	private static PortingHoursToMinutes(timeStr: string): number {
		const [hours, minutes, _] = timeStr
			.split(":")
			.map((val) => Number.parseInt(val, 10));

		if (hours < 0 || hours > 23 || minutes < 0 || minutes > 60) {
			return 0;
		}

		return hours * 60 + minutes;
	}

	private static DayPortingHoursToMinutes(dayPortingHours: IDayPortingHours): {
		startMinutes: number;
		endMinutes: number;
	} {
		if (!dayPortingHours.startTime || !dayPortingHours.endtime) {
			return {
				startMinutes: 0,
				endMinutes: 0,
			};
		}

		return {
			startMinutes: PortingConfig.PortingHoursToMinutes(
				dayPortingHours.startTime,
			),
			endMinutes: PortingConfig.PortingHoursToMinutes(dayPortingHours.endtime),
		};
	}

	public isInPortingHours(timestamp: Date | Moment): boolean {
		const _timestamp = moment.tz(timestamp, TIMEZONE);
		const dayOfWeek = PortingConfig.getDayOfWeek(_timestamp);

		const dayPortingHours = this.weekPortingHours.find(
			(el) => el.day === dayOfWeek,
		);

		if (!dayPortingHours) {
			return false;
		}

		const { startMinutes, endMinutes } =
			PortingConfig.DayPortingHoursToMinutes(dayPortingHours);

		if (
			(startMinutes === 0 && endMinutes === 0) ||
			startMinutes >= endMinutes
		) {
			return false;
		}

		const timestampDayMinutes = _timestamp.minutes() + _timestamp.hours() * 60;

		return (
			startMinutes <= timestampDayMinutes &&
			timestampDayMinutes <= endMinutes - 90
		);
	}
}

export default PortingConfig;
